/**
 * kb-sync-dialog.tsx — "Lấy nội dung từ nguồn ngoài" vào một kho: chọn nguồn (Google Sheets / Google Docs / trang web
 * dùng luồng nạp có sẵn; nguồn khác "Sắp có").
 */
import { useTranslation } from 'react-i18next'
import { FileSpreadsheet, FileText, Globe, Layers, ShoppingCart, BookMarked } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { AddKnowledgeTab } from '../add-knowledge-dialog'

type Source = { id: string; icon: React.ReactNode; tab?: AddKnowledgeTab; tint: string }

const SOURCES: Source[] = [
  { id: 'gsheet', icon: <FileSpreadsheet className="h-5 w-5" />, tab: 'sheet', tint: 'bg-success/10 text-success' },
  { id: 'gdoc', icon: <FileText className="h-5 w-5" />, tab: 'link', tint: 'bg-primary/10 text-primary' },
  { id: 'web', icon: <Globe className="h-5 w-5" />, tab: 'link', tint: 'bg-warning/10 text-warning' },
  { id: 'notion', icon: <BookMarked className="h-5 w-5" />, tint: 'bg-muted text-muted-foreground' },
  { id: 'site', icon: <Layers className="h-5 w-5" />, tint: 'bg-muted text-muted-foreground' },
  { id: 'shop', icon: <ShoppingCart className="h-5 w-5" />, tint: 'bg-muted text-muted-foreground' },
]

export function KbSyncDialog({ onClose, onPick }: { onClose: () => void; onPick: (tab: AddKnowledgeTab) => void }) {
  const { t } = useTranslation('aiKnow')
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('sync.title')}</DialogTitle>
          <DialogDescription>{t('sync.description')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          {SOURCES.map((s) => {
            const soon = !s.tab
            return (
              <button
                key={s.id} type="button" disabled={soon}
                onClick={() => { if (s.tab) { onClose(); onPick(s.tab) } }}
                className={cn(
                  'flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors',
                  soon ? 'cursor-not-allowed opacity-60' : 'hover:border-primary/60 hover:bg-primary/5',
                )}
              >
                <span className={cn('flex h-9 w-9 items-center justify-center rounded-lg', s.tint)}>{s.icon}</span>
                <span className="flex items-center gap-1.5 text-sm font-medium">{t(`sync.sources.${s.id}.name`)}{soon && <Badge variant="secondary" className="text-[10px]">{t('sync.soon')}</Badge>}</span>
                <span className="text-xs text-muted-foreground">{t(`sync.sources.${s.id}.desc`)}</span>
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
